import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Collapse,
  Fade
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Settings as SettingsIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { getWebhookConfigs, checkWebhookStatusInShopify } from '../services/api';
import api from '../services/api';
import ServerDataTable from './ServerDataTable/ServerDataTable';
import { formatDate } from '../utils/formatDate';
import ConfirmDialog from './ConfirmDialog';
import StatusBadge from './StatusBadge';

export default function WebhookModal({
  open,
  onClose,
  activeConfig,
  webhookResult,
  setWebhookResult,
  showWebhookResult,
  setShowWebhookResult
}) {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'desc'
  });
  const [isRegisteringWebhooks, setIsRegisteringWebhooks] = useState(false);
  const [isRegisterWebhooksDialogOpen, setIsRegisterWebhooksDialogOpen] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [statusCheckResult, setStatusCheckResult] = useState(null);
  const [showStatusResult, setShowStatusResult] = useState(false);
  const [alertTransitions, setAlertTransitions] = useState({
    webhookResult: true,
    statusResult: true
  });

  // Fetch webhook configurations for the active config
  const { data: webhookData, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['webhookConfigs', activeConfig?.id, pageIndex, pageSize, sortConfig],
    queryFn: async () => {
      if (!activeConfig?.id) return { items: [], pagination: { total: 0 } };

      const params = {
        page: pageIndex,
        limit: pageSize,
        shopify_config_id: activeConfig.id,
        sortBy: sortConfig.key || 'created_at',
        sortOrder: sortConfig.direction?.toUpperCase() || 'DESC'
      };

      const response = await getWebhookConfigs(params);
      return response.data.data;
    },
    enabled: open && !!activeConfig?.id,
    retry: false,
    refetchOnWindowFocus: false
  });

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
    setPageIndex(0);
  };

  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageIndex(0);
  };

  const handleRefresh = () => {
    refetch();
  };

  // Register webhooks handler
  const handleRegisterWebhooks = async () => {
    if (!activeConfig?.id) return;

    try {
      setIsRegisteringWebhooks(true);
      setWebhookResult(null);
      setShowWebhookResult(false);

      const response = await api.post(`/webhook-config/shopify-config/${activeConfig.id}/register-webhooks`);

      if (response.data) {
        setWebhookResult({
          success: response.data.status === 'success' || response.data.status === 'partial',
          message: response.data.message,
          details: response.data.results
        });
      } else {
        setWebhookResult({
          success: false,
          message: 'No response from server'
        });
      }

      setShowWebhookResult(true);
      // Reset transition state for animation
      setAlertTransitions(prev => ({ ...prev, webhookResult: true }));
      // Refresh the webhook list after registration
      refetch();
    } catch (error) {
      console.error('Register webhooks error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';

      setWebhookResult({
        success: false,
        message: `Failed to register webhooks: ${errorMessage}`
      });

      setShowWebhookResult(true);
      // Reset transition state for animation
      setAlertTransitions(prev => ({ ...prev, webhookResult: true }));
    } finally {
      setIsRegisteringWebhooks(false);
      setIsRegisterWebhooksDialogOpen(false);
    }
  };

  // Check webhook status in Shopify
  const handleCheckWebhookStatus = async () => {
    if (!activeConfig?.id) return;

    try {
      setIsCheckingStatus(true);
      setStatusCheckResult(null);
      setShowStatusResult(false);

      const response = await checkWebhookStatusInShopify(activeConfig.id);

      setStatusCheckResult(response.data);
      setShowStatusResult(true);
      // Reset transition state for animation
      setAlertTransitions(prev => ({ ...prev, statusResult: true }));

      // Refresh the webhook list to show updated statuses
      refetch();
    } catch (error) {
      console.error('Check webhook status error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';

      setStatusCheckResult({
        success: false,
        message: `Failed to check webhook status: ${errorMessage}`,
        data: {
          results: [],
          summary: { total: 0, active: 0, inactive: 0, notFound: 0, errors: 1 }
        }
      });

      setShowStatusResult(true);
      // Reset transition state for animation
      setAlertTransitions(prev => ({ ...prev, statusResult: true }));
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 10,
      align: 'center'
    },
    {
      field: 'topic',
      headerName: 'Topic',
      width: 100,
      renderCell: ({ value }) => (
        <StatusBadge
          customize={true}
          customLabel={value}
          customColor={'info'}
        />
      )
    },
    {
      field: 'address',
      headerName: 'Webhook URL',
      width: 300,
      renderCell: ({ value }) => (
        <Typography variant='body2' fontFamily='monospace' noWrap>
          {value || 'N/A'}
        </Typography>
      )
    },
    {
      field: 'format',
      headerName: 'Format',
      width: 70,
      renderCell: ({ value }) => (
        <StatusBadge
          customize={true}
          customLabel={value}
          customColor={'info'}
        />
      )
    },
    {
      field: 'shopify_webhook_id',
      headerName: 'Shopify ID',
      width: 150,
      renderCell: ({ value }) => (
        <Typography variant='body2' fontFamily='monospace'>
          {value || 'N/A'}
        </Typography>
      )
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      renderCell: ({ value }) => (
        <StatusBadge
          customize={true}
          customLabel={value ? 'Active' : 'Inactive'}
          customColor={'info'}
        />
      )
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 170,
      renderCell: ({ value }) => (
        <Typography variant='body2'>
          {formatDate(value)}
        </Typography>
      )
    },
    {
      field: 'updated_at',
      headerName: 'Updated At',
      width: 170,
      renderCell: ({ value }) => (
        <Typography variant='body2'>
          {formatDate(value)}
        </Typography>
      )
    }
  ];

  const handleClose = () => {
    setWebhookResult(null);
    setShowWebhookResult(false);
    setStatusCheckResult(null);
    setShowStatusResult(false);
    setAlertTransitions({ webhookResult: true, statusResult: true });
    onClose();
  };

  const handleCloseWebhookAlert = () => {
    setAlertTransitions(prev => ({ ...prev, webhookResult: false }));
    setTimeout(() => {
      setShowWebhookResult(false);
      setWebhookResult(null);
      setAlertTransitions(prev => ({ ...prev, webhookResult: true }));
    }, 300);
  };

  const handleCloseStatusAlert = () => {
    setAlertTransitions(prev => ({ ...prev, statusResult: false }));
    setTimeout(() => {
      setShowStatusResult(false);
      setStatusCheckResult(null);
      setAlertTransitions(prev => ({ ...prev, statusResult: true }));
    }, 300);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='lg'
        fullScreen
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          p: { xs: '15px !important', sm: '15px !important', md: '20px !important' }
        }}>
          <Typography variant='h6' component='div'>
            Webhook Configurations
            {activeConfig && (
              <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                Store: {activeConfig.store_name || activeConfig.storeName} ({activeConfig.store_url || activeConfig.storeUrl})
              </Typography>
            )}
          </Typography>
          <Box display='flex' alignItems='center' gap={1}>
            <Tooltip title='Close'>
              <IconButton onClick={handleClose} size='small'>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        <DialogContent sx={{
          px: { xs: '10px !important', sm: '10px !important', md: '20px !important' },
          py: '0 !important',
          flex: 1
        }}>
          {/* Alert sections with controlled height */}
          {(showWebhookResult && webhookResult) || (showStatusResult && statusCheckResult) ? (
            <Box sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              py: { xs: '10px !important', sm: '10px !important', md: '20px !important' },
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: '10px !important', sm: '10px !important', md: '20px !important' }
            }}>
              {showWebhookResult && webhookResult && (
                <Collapse
                  in={alertTransitions.webhookResult}
                  timeout={300}
                  unmountOnExit
                >
                  <Box>
                    <Alert
                      severity={webhookResult.success ? 'success' : 'error'}
                      action={
                        <Tooltip title='Dismiss'>
                          <IconButton
                            aria-label='close'
                            color='inherit'
                            size='small'
                            onClick={handleCloseWebhookAlert}
                          >
                            <ClearIcon fontSize='inherit' />
                          </IconButton>
                        </Tooltip>
                      }
                    >
                      <AlertTitle>
                        {webhookResult.success ? 'Webhook Registration Result' : 'Registration Failed'}
                      </AlertTitle>
                      <Typography variant='body2' sx={{ mb: 1 }}>
                        {webhookResult.message}
                      </Typography>
                      {webhookResult.details && webhookResult.details.length > 0 && (
                        <List dense sx={{ p: 0, m: 0 }}>
                          {webhookResult.details.map((detail, index) => (
                            <ListItem key={index} sx={{ p: '0 !important' }}>
                              <ListItemText
                                primary={
                                  <>
                                    <Box display='flex' alignItems='center' gap={1} sx={{ width: '100%' }}>
                                      {detail.success ? (
                                        <CheckCircleOutlineIcon color='success' fontSize='small' />
                                      ) : (
                                        <Typography color='error' component='span'>✗</Typography>
                                      )}
                                      <Box display='flex' alignItems='center' gap={1} border={1} p={1} borderRadius={1} sx={{ width: '100%', borderColor: detail.success ? 'success.main' : 'error.main' }}>
                                        <Typography variant='body2' sx={{ display: 'flex', flexDirection: 'column', gap: '2px', color: detail.success ? 'success.main' : 'error.main' }}>
                                          <Typography variant='body'>
                                            Topic: {detail.topic}
                                          </Typography>
                                          <Typography variant='body' sx={{ wordBreak: 'break-all' }}>
                                            Address: {detail.address}
                                          </Typography>
                                          <Typography variant='body'>
                                            Status: {detail.status}
                                          </Typography>
                                          <Typography variant='body'>
                                            Message: {detail.message}
                                          </Typography>
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Alert>
                  </Box>
                </Collapse>
              )}

              {showStatusResult && statusCheckResult && (
                <Collapse
                  in={alertTransitions.statusResult}
                  timeout={300}
                  unmountOnExit
                >
                  <Box>
                    <Alert
                      severity={statusCheckResult.success ? 'info' : 'error'}
                      action={
                        <Tooltip title='Dismiss'>
                          <IconButton
                            aria-label='close'
                            color='inherit'
                            size='small'
                            onClick={handleCloseStatusAlert}
                          >
                            <ClearIcon fontSize='inherit' />
                          </IconButton>
                        </Tooltip>
                      }
                    >
                      <AlertTitle>
                        Webhook Status Check Results
                      </AlertTitle>
                      <Typography variant='body2' sx={{ mb: 2 }}>
                        {statusCheckResult.message}
                      </Typography>
                      {statusCheckResult.data?.summary && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant='body2' fontWeight='bold' gutterBottom>
                            Summary:
                          </Typography>
                          <Box display='flex' gap={2} flexWrap='wrap'>
                            <StatusBadge
                              customize={true}
                              customLabel={`Total: ${statusCheckResult.data.summary.total}`}
                              customColor={'info'}
                            />
                            <StatusBadge
                              customize={true}
                              customLabel={`Found: ${statusCheckResult.data.summary.found}`}
                              customColor={'success'}
                            />
                            <StatusBadge
                              customize={true}
                              customLabel={`Not Found: ${statusCheckResult.data.summary.notFound}`}
                              customColor={'error'}
                            />
                            {statusCheckResult.data.summary.errors > 0 && (
                              <StatusBadge
                                customize={true}
                                customLabel={`Errors: ${statusCheckResult.data.summary.errors}`}
                                customColor={'error'}
                              />
                            )}
                          </Box>
                        </Box>
                      )}
                      {statusCheckResult.data?.results && statusCheckResult.data.results.length > 0 && (
                        <List dense sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                          {statusCheckResult.data.results.map((result, index) => (
                            <ListItem key={index} sx={{ p: 0 }}>
                              <ListItemText
                                primary={
                                  <Box display='flex' alignItems='start' gap={1} flexWrap='wrap'>
                                    {result.status === 'found' ? (
                                      <CheckCircleOutlineIcon color='success' fontSize='small' />
                                    ) : result.status === 'not_found' ? (
                                      <Typography color='warning.main' component='span'>⚠</Typography>
                                    ) : (
                                      <Typography color='error' component='span'>✗</Typography>
                                    )}
                                    <Box display='flex' alignItems='' gap={0} flexDirection='column'>
                                      <Typography variant='body2' component='span'>
                                        <strong>{result.topic}</strong>
                                      </Typography>
                                      <Typography variant='body2' component='span'>
                                        (ID: {result.shopify_webhook_id}):
                                      </Typography>
                                    </Box>
                                    <StatusBadge
                                      customize={true}
                                      customLabel={result.status === 'found' ? 'Found' : result.status === 'not_found' ? 'Not Found' : 'Error'}
                                      customColor={result.status === 'found' ? 'success' : result.status === 'not_found' ? 'warning' : 'error'}
                                    />
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Alert>
                  </Box>
                </Collapse>
              )}
            </Box>
          ) : null}

          {/* Main table area with proper scrolling */}
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}>
            {/* Action buttons */}
            <Box sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
              py: { xs: '10px !important', sm: '10px !important', md: '10px !important' }
            }}>
              <Box display='flex' gap={2} sx={{
                width: '100%',
                flexWrap: 'wrap',
                flexDirection: { xs: 'column', md: 'row' },
                py: { xs: '10px !important', sm: '10px !important', md: '10px !important' }
              }}>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<SettingsIcon />}
                  onClick={() => setIsRegisterWebhooksDialogOpen(true)}
                  disabled={isRegisteringWebhooks || !activeConfig || isCheckingStatus || isLoading || isFetching}
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  {isRegisteringWebhooks ? 'Registering...' : 'Register Webhooks'}
                </Button>
                <Button
                  variant='contained'
                  startIcon={<CheckCircleOutlineIcon />}
                  onClick={handleCheckWebhookStatus}
                  disabled={isCheckingStatus || !activeConfig || (webhookData?.items?.length === 0) || isLoading || isFetching || isRegisteringWebhooks}
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  {isCheckingStatus ? 'Checking...' : 'Check Status in Shopify'}
                </Button>
                <Button
                  variant='contained'
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  disabled={isLoading || isFetching || isRegisteringWebhooks || isCheckingStatus}
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                >
                  Refresh
                </Button>
              </Box>
            </Box>

            {/* Table container with proper height and scrolling */}
            <Box sx={{
              flex: 1,
              minHeight: 0,
              position: 'relative'
            }}>
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0
              }}>
                <ServerDataTable
                  data={webhookData?.items || []}
                  columns={columns}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  totalRows={webhookData?.pagination?.total || 0}
                  currentPage={pageIndex}
                  rowsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handlePageSizeChange}
                  rowsPerPageOptions={[10, 25, 50]}
                  loading={isLoading || isFetching}
                  isError={error}
                  error={error}
                  noDataMessage='No webhook configurations found'
                  totalPages={Math.ceil((webhookData?.pagination?.total || 0) / pageSize)}
                  enableSearch={false}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          flexShrink: 0
        }}>
          <Button onClick={handleClose} variant='contained' color='secondary'>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={isRegisterWebhooksDialogOpen}
        title='Register Webhooks'
        message='Are you sure you want to register webhooks for this store? This will create or update webhooks for the store.'
        confirmColor='primary'
        onConfirm={handleRegisterWebhooks}
        onCancel={() => setIsRegisterWebhooksDialogOpen(false)}
        isLoading={isRegisteringWebhooks}
        confirmText='Register Webhooks'
      />
    </>
  );
}
